import { FootPrintPadHoleType, LayerOrientation, PcbLayoutFootprintPadShape, PcbNodeTypes, } from "@buildwithflux/models";
import { metersToMm, normalizeRadians, radiansToDegrees } from "@buildwithflux/shared";
import { Euler, Matrix4, Vector4 } from "three";
import { Clipper, JoinType } from "../../../../clipper";
import { geom2, removePolyHoles, vec2 } from "../../../../common";
import { CurveDivisionsForExport } from "../../../../fills";
import { createCircleOrOblongShape, createShapeForPad, createShapeFromBakedRules } from "../../../../shapes";
import { KicadPadType } from "../../../importers/kicad/KicadTypes";
import { fixAngleDecimals, getModule, toMm } from "./support/utils";
// Test project
// http://localhost:3000/support/gerber-pad-rotation-axis-support?editor=pcb_2d
function KiCadPtBuilder(params) {
    let _pts = geom2.polygonRemoveLoopbackEdges("shape" in params
        ? params.shape.getPoints(CurveDivisionsForExport).map((point) => ({
            x: toMm(point.x),
            y: toMm(point.y) * -1, // Negate to map into KiCad coordinate space
        }))
        : params.polygon);
    const builder = {
        translate: function (vector) {
            _pts = _pts.map((point) => vec2.add(point, vector));
            return builder;
        },
        scale: function (vector) {
            _pts = _pts.map((point) => vec2.scale(point, vector));
            return builder;
        },
        transform: function (matrix) {
            _pts = _pts.map((point) => {
                const t = new Vector4(point.x, point.y, 0, 1).applyMatrix4(matrix);
                return {
                    x: t.x,
                    y: t.y,
                };
            });
            return builder;
        },
        expand: function (amount, options) {
            if (amount !== 0) {
                const offset = metersToMm(amount);
                _pts = Clipper.shape([_pts], true)
                    .offset(offset, {
                    // For consistency with createShapeFromSvgString(..). Note that rounded join type is
                    // problematic for the tracespace CLI. See comments below
                    joinType: JoinType.Round,
                    ...options,
                })
                    .toPolygons()
                    .flatMap((path) => path);
                _pts = geom2.polygonRemoveColocatedVertices(_pts, {
                    // This epsilon is somewhat arbitrary. It was determined to be the smallest value
                    // that would allow the cypress test for gerber export of 'testgerber-netlist-export'
                    // to return a reasonable gerber, with Rounded joinType, and a similar amount of geometric
                    // complexity to input geometry.
                    // Without this geometry simplification, rounded joints would at times consit of > 1000
                    // vertices, leading to the tracespace CLI (in cypress test) crashing.
                    // Rather than send thousands of vertices to the kicad processor, this filter reduces overhead
                    // from the client
                    epsilon: 0.05,
                });
            }
            return builder;
        },
        /**
         * Removes a hole from the current shape. This is necessary for properly supporting solder mask expansion that is relative to the
         * hole edge, as KiCad doesn't support holes in custom pad shapes, so we need to add holes as separate polygons and then remove
         * them from the main pad shape using this method.
         *
         * Note this method returns an array of builders, as subtracting the hole may result in multiple perimeters if the hole intersects
         * with the pad shape in such a way that it creates multiple separate shapes. In practice this is unlikely to occur with typical
         * solder mask expansion values and hole sizes, but it's possible with extreme values. In any case, we need to be able to handle
         * this scenario, hence the array return type.
         *
         * Returning a builder ensures utilities like transform and expand can still be applied to the resulting shapes if needed.
         *
         * @param x
         * @param y
         * @param width
         * @param height
         * @returns
         */
        hole: function (x, y, width, height) {
            const shapeHole = Clipper.fromShape(createCircleOrOblongShape({
                x: metersToMm(width),
                y: metersToMm(height),
            }, { x: metersToMm(x), y: -metersToMm(y) }, 0, CurveDivisionsForExport));
            const shapes = Clipper.shape([_pts], true).subtract([shapeHole]).separateShapes();
            const polygons = shapes.reduce((polygons, shape) => {
                const [first, ...rest] = shape.toPolygons();
                if (first) {
                    polygons.push(removePolyHoles(first, rest));
                }
                return polygons;
            }, []);
            return polygons.map((polygon) => KiCadPtBuilder({ polygon }));
        },
        pts: function () {
            return _pts;
        },
    };
    return builder;
}
const getKiCadCopperLayersForType = (pad, nodes, context) => {
    const bakedRules = pad.bakedRules;
    const padType = getKiCadPadType(bakedRules);
    switch (padType) {
        case "smd":
        case "connect": {
            return bakedRules.layer === LayerOrientation.top ? ["F.Cu"] : ["B.Cu"];
        }
        case "thru_hole": {
            // Since we can't target all copper layers with a single selector; *.Cu, we need to handle the "Connected all layers"
            // case by first obtaining all copper layers in the stackup and then, from these, getting the corresponding kicad layer
            // identifiers via getKiCadLayerByLayerIdentifier(..). This is suboptimal and will result in larger kicad files, but
            // necessary with the custom-shaped-centric pads mapping logic we have in place.
            const isConnectedAllCopper = !bakedRules.connectedLayers || bakedRules.connectedLayers.includes("All");
            const connectedLayers = bakedRules.connectedLayers ?? [];
            if (!pad.bakedRules.rootFootprintOrLayoutUid) {
                throw new Error(`Cannot find root layout for node ${pad.uid} at ${pad.bakedRules.rootFootprintOrLayoutUid}`);
            }
            const rootLayout = nodes[pad.bakedRules.rootFootprintOrLayoutUid];
            if (rootLayout?.type !== PcbNodeTypes.layout && rootLayout?.type !== PcbNodeTypes.footprint) {
                throw new Error(`Cannot find root layout for node ${pad.uid} at ${pad.bakedRules.rootFootprintOrLayoutUid}`);
            }
            const connectedLayerIdentifiers = isConnectedAllCopper
                ? Object.values(rootLayout.bakedRules.stackup ?? {}).map((layer) => layer.name)
                : // Annoyingly, the connected layers array might be proxies and not mutable. Wrapping items in a new array allows
                    // subsequent mutation is needed
                    Array.from(connectedLayers);
            // STD Pads should always be connected to the top and bottom layers - if Top and Bottom are not present in connected
            // layers, include their identifier so kicad layer for these can be derived
            if (!connectedLayerIdentifiers.some((layer) => layer === "Top")) {
                connectedLayerIdentifiers.push("Top");
            }
            if (!connectedLayerIdentifiers.some((layer) => layer === "Bottom")) {
                connectedLayerIdentifiers.push("Bottom");
            }
            return (connectedLayerIdentifiers
                .map((fluxLayer) => context.getKiCadLayerByLayerIdentifier(pad.uid, fluxLayer))
                // Just remove connected layers that don't exist in the layer stack
                // We already report this case in DRC
                .filter((layer) => layer !== null));
        }
        case "np_thru_hole": {
            return [];
        }
    }
};
function getKiCadPadType(br) {
    switch (br.hole?.holeType) {
        case FootPrintPadHoleType.SurfaceMountDevice:
            return "smd";
        case FootPrintPadHoleType.nonPlatedHole:
            return "np_thru_hole";
        case FootPrintPadHoleType.testPinOrCardEdgeConnector:
            return "connect";
        case FootPrintPadHoleType.platedThroughHole:
        default:
            return "thru_hole";
    }
}
/* KiCad utilities. Converts from meters to millimeters */
const getKiCadDrill = (bakedRules) => {
    const hole = bakedRules.hole;
    if (!hole) {
        return;
    }
    return {
        width: toMm(hole.holeSize.x),
        height: toMm(hole.holeSize.y),
        offset: {
            x: 0,
            y: 0,
        },
        oval: hole.holeSize.x !== hole.holeSize.y,
    };
};
const getKiCadAt = (bakedRules) => {
    const hole = bakedRules.hole;
    const holePosition = {
        x: hole?.holePosition.x ?? 0,
        y: hole?.holePosition.y ?? 0,
    };
    const rotation = new Matrix4().makeRotationFromEuler(new Euler(bakedRules.rotationRelativeToDocument.x, bakedRules.rotationRelativeToDocument.y, -bakedRules.rotationRelativeToDocument.z));
    const offset = new Vector4(toMm(holePosition.x), toMm(-holePosition.y), 0, 1).applyMatrix4(rotation);
    // Compute angle of pad - this is now just for hole rotation. Usually hole rotation doesn't make much difference,
    // the exception being non-circular holes, where rotation of the hole itself actually matters
    const normX = normalizeRadians(bakedRules.rotationRelativeToDocument.x, true);
    const flippedAroundXAxis = Math.abs(normX) > Math.PI / 2;
    let angle = bakedRules.rotationRelativeToDocument.z + (flippedAroundXAxis ? Math.PI : 0);
    if (bakedRules.layer === LayerOrientation.bottom) {
        angle = normalizeRadians(-angle);
    }
    return {
        x: toMm(bakedRules.positionRelativeToDocument.x) + offset.x,
        y: -toMm(bakedRules.positionRelativeToDocument.y) + offset.y,
        angle: fixAngleDecimals(radiansToDegrees(angle)),
    };
};
export const mapPad = (pad, context, postprocess, _nodes) => {
    const bakedRules = pad.bakedRules;
    if (!bakedRules) {
        return null;
    }
    const at = getKiCadAt(bakedRules);
    const padType = getKiCadPadType(bakedRules);
    const copperShape = createShapeFromBakedRules(bakedRules, 0, {
        polygonSegments: CurveDivisionsForExport,
    });
    if (!copperShape) {
        return null;
    }
    const rotation = new Matrix4().makeRotationFromEuler(new Euler(bakedRules.rotationRelativeToDocument.x, bakedRules.rotationRelativeToDocument.y, -bakedRules.rotationRelativeToDocument.z));
    const translation = new Matrix4().makeTranslation(toMm(bakedRules.positionRelativeToDocument.x), -toMm(bakedRules.positionRelativeToDocument.y), 0);
    const scale = new Matrix4().makeScale(bakedRules.scale.x, bakedRules.scale.y, 1);
    const transform = new Matrix4();
    transform.multiply(translation);
    transform.multiply(rotation);
    transform.multiply(scale);
    // Because KiCad has limited support for custom solder mask shapes
    // we add them as separate polygons afterwards
    postprocess((mapped) => {
        if (!mapped) {
            return;
        }
        const module = getModule(bakedRules.layer, context);
        module.pad = module.pad ?? [];
        module.pad.push(mapped);
        module.fp_poly = module.fp_poly ?? [];
        const holeX = bakedRules.hole?.holePosition.x ?? 0;
        const holeY = bakedRules.hole?.holePosition.y ?? 0;
        const holeW = bakedRules.hole?.holeSize.x ?? 0;
        const holeH = bakedRules.hole?.holeSize.y ?? 0;
        const appendSolderMask = (side) => {
            const layer = side === LayerOrientation.top ? "F.Mask" : "B.Mask";
            // Hack: The UI doesn't properly handle solder mask expansion
            // for SMD pads on the bottom. To align with the UI and keep Gerbers
            // consistent, yet broken :-(, we do the same thing the UI does and always
            // use the top solder mask expansion in this case
            // const uiAlignedSide = padType === "smd" ? LayerOrientation.top : LayerOrientation.bottom;
            const shape = createShapeForPad({
                bakedRules,
                segments: CurveDivisionsForExport,
            });
            if (!shape) {
                return;
            }
            const scale = bakedRules.scale.x ?? 1;
            const expansion = ((side === LayerOrientation.top
                ? bakedRules.solderMaskExpansion?.topExpansion
                : bakedRules.solderMaskExpansion?.bottomExpansion) ?? 0) * scale;
            const expansionFromTheHoleEdge = bakedRules.solderMaskExpansion?.expansionFromTheHoleEdge ?? 0;
            if (Math.abs(expansionFromTheHoleEdge) > expansion) {
                // Since hole edges are known to be circular, when expansion from hole edge is greater than overall
                // expansion, this effectivly removes all solder mask around the hole resulting in no solder mask
                // geometry. In that case, early exit to avoid further solder mask computation
                return;
            }
            let builder = KiCadPtBuilder({ shape }).expand(expansion, {
                joinType: JoinType.Round,
            });
            if (expansionFromTheHoleEdge < 0 && holeW > 0 && holeH > 0) {
                // Negative expansion from hole edge for SMD / STD pads causes hole in solder mask geometry to grow outwards:
                // https://www.figma.com/design/KJtwglIeAa2BZ10qySohru?node-id=13-61859#1625708908
                for (const perimeterBuilder of builder.hole(holeX, holeY, holeW - expansionFromTheHoleEdge * 2, holeH - expansionFromTheHoleEdge * 2)) {
                    const pts = perimeterBuilder.transform(transform).pts();
                    module.fp_poly?.push({
                        pts,
                        layer,
                        width: 0,
                    });
                }
            }
            else {
                builder = builder.transform(transform);
                const pts = builder.pts();
                module.fp_poly?.push({
                    pts,
                    layer,
                    width: 0,
                });
            }
        };
        const appendSolderPaste = (side) => {
            const shape = createShapeForPad({
                bakedRules,
                segments: CurveDivisionsForExport,
            });
            if (!shape) {
                return;
            }
            const scale = bakedRules.scale.x ?? 1;
            const expansion = (bakedRules.solderPasteMaskExpansion ?? 0) * scale;
            const layer = side === LayerOrientation.top ? "F.Paste" : "B.Paste";
            const pts = KiCadPtBuilder({ shape })
                .transform(transform)
                .expand(expansion, {
                joinType: bakedRules.padShape === PcbLayoutFootprintPadShape.rectangle ? JoinType.Round : JoinType.Miter,
            })
                .pts();
            module.fp_poly?.push({
                pts,
                layer,
                width: 0,
            });
        };
        // For each copper layer that this pad is connected, iterate and append generated copper geometry to the collection of
        // footprint polygons in the layout. This is basically an "escape hatch" that allows us to generate custom copper geometry
        // (with arbitrary expansion, scaling, shape, etc) on a per-pad, per-layer basis. For this flexibility though there is a
        // catch; we can't target footprint polygon geometry for all copper layers using the *.Cu selector. We need to target each
        // layer individually, F.Cu, In1.Cu, etc
        for (const layer of getKiCadCopperLayersForType(pad, _nodes, context)) {
            module.fp_poly.push({
                pts: KiCadPtBuilder({ shape: copperShape }).transform(transform).pts(),
                layer,
                width: 0,
            });
        }
        switch (padType) {
            case KicadPadType.thruHole: {
                appendSolderMask(LayerOrientation.top);
                appendSolderMask(LayerOrientation.bottom);
                appendSolderPaste(LayerOrientation.top);
                appendSolderPaste(LayerOrientation.bottom);
                break;
            }
            case KicadPadType.nonPlatedThruHole: {
                appendSolderMask(LayerOrientation.top);
                appendSolderMask(LayerOrientation.bottom);
                break;
            }
            case KicadPadType.connect:
            case KicadPadType.surfaceMountDevice: {
                if (bakedRules.layer === LayerOrientation.top || bakedRules.layer === LayerOrientation.bottom) {
                    appendSolderPaste(bakedRules.layer);
                    appendSolderMask(bakedRules.layer);
                }
                break;
            }
        }
        // Note: np_thru_hole pads don't need solder mask or paste
    });
    return {
        pad_shape: "custom",
        primitives: [],
        size: { width: 0, height: 0 },
        at,
        pad_id: pad.name,
        pad_type: getKiCadPadType(bakedRules),
        layers: getKiCadCopperLayersForType(pad, _nodes, context),
        drill: getKiCadDrill(bakedRules),
        locked: false,
    };
};
//# sourceMappingURL=mapPad.js.map